import React, { useEffect, useId, useRef, useState } from 'react';
import { Dropdown } from 'antd';

// Keep the same AntD appearance as other DataVue menus; only constrain long lists to the viewport.
const overlayClass = [
  'max-w-[calc(100vw-32px)]',
  '[&_.ant-dropdown-menu]:max-h-[min(60dvh,360px)] [&_.ant-dropdown-menu]:overflow-y-auto',
  '[&_.ant-dropdown-menu-item]:truncate',
].join(' ');

/** AntD 3 menus handle arrows/Enter after focus enters the popup. */
export default function MailerOptionsDropdown({ overlay, children, disabled = false, visible, onVisibleChange, placement, className = '', label = 'Mailer options' }) {
  const [uncontrolledOpen, setOpen] = useState(false);
  const open = visible === undefined ? uncontrolledOpen : visible;
  const host = useRef(null), openUpward = useRef(false);
  const id = useId();
  const changeOpen = value => {
    if (visible === undefined) setOpen(value);
    onVisibleChange?.(value);
  };
  const close = restoreFocus => {
    changeOpen(false);
    if (restoreFocus) host.current?.querySelector('button')?.focus();
  };
  useEffect(() => {
    if (disabled) { changeOpen(false); return undefined; }
    if (!open) return undefined;
    let observer;
    const timer = setTimeout(() => {
      const menu = document.getElementById(id);
      if (!menu) return;
      // AntD owns the active item; expose that same item to assistive technology.
      const announceActiveItem = () => {
        menu.querySelectorAll('[role="menuitem"]').forEach((item, index) => { item.id = id + '-item-' + index; });
        const active = menu.querySelector('.ant-dropdown-menu-item-active');
        if (active) menu.setAttribute('aria-activedescendant', active.id);
        else menu.removeAttribute('aria-activedescendant');
      };
      observer = new MutationObserver(announceActiveItem);
      observer.observe(menu, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      announceActiveItem();
      menu?.focus();
      if (openUpward.current) menu?.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', keyCode: 38, which: 38, bubbles: true, cancelable: true }));
    }, 0);
    return () => { clearTimeout(timer); observer?.disconnect(); };
  }, [open, disabled, id]);
  const menu = React.cloneElement(overlay, {
    id, defaultActiveFirst: true, 'aria-label': overlay.props['aria-label'] || label,
    onClick: event => { close(true); overlay.props.onClick?.(event); },
    onKeyDownCapture: event => {
      if (event.key === ' ') {
        event.preventDefault();
        event.currentTarget.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true }));
        return;
      }
      if (event.key !== 'Escape' && event.key !== 'Tab') return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
      close(true);
    },
  });
  return <span ref={host} className={'inline-flex ' + className}><Dropdown overlay={menu} overlayClassName={overlayClass} trigger={['click']} placement={placement} disabled={disabled} visible={open && !disabled} destroyPopupOnHide onVisibleChange={value => { openUpward.current = false; changeOpen(value); }}>
    {React.cloneElement(children, { 'aria-haspopup': 'menu', 'aria-expanded': open && !disabled, 'aria-controls': open ? id : undefined, onKeyDown: event => {
      if (disabled || !['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault(); openUpward.current = event.key === 'ArrowUp'; changeOpen(true);
    } })}
  </Dropdown></span>;
}
